import React, { Component } from "react"
import { CATEGORY, COLOR, COLUMN, FORMATTING, JARGON, LAYOUT } from "../../modules/constants"
import initStats, { initStats2 } from "../../components/layout/InitStats"
import NumerableTable from "../../components/NumerableTable"
import NoDataMessage from "../../components/searchCompanies/NoDataMessage"
import { addPaddingRightToLastCell, calcStats, getCell, getCellWithTooltip, headCell, setStats } from "../../modules/table.funcs"
import { formatInteger } from "../../modules/utilities"

const { border1, center, integer, left, right, string } = FORMATTING
const borderLeft = border1
const stats2 = {}

// This component renders the table of an adviser's clients, which is shown when the user clicks on the number of clients in the advisers list table.
// It receives a list of companies as props, which may be filtered to only include companies that have the adviser in an adviser role,
// and then creates the headers, rows and footers for the table based on that list of companies and other props.
class NexusAdviserClientsTable extends Component {
  render() {
    // console.log("NexusAdviserClientsTable this.props: ", this.props)
    const { clients, handleClickFavouriteIcon, handleSelect, items, kind, showIndex } = this.props
    const stats = initStats()
    const tableProps = { ...this.props, clients, showIndex, stats }

    return (
      <React.Fragment>
        {items?.length < 1 &&
          <NoDataMessage kind={kind} />}

        {/* items[0] is the company */}
        {
          (<NumerableTable table={{
            ...this.props,
            headers: createNexusAdviserClientsHeadersData(tableProps),
            rows: createAdvisersListRowData({ ...tableProps }),
            footers: createNexusAdviserClientsFootersData(tableProps),
            handleOnClick: row => handleSelect(row.link),
            handleClickFavouriteIcon: row => handleClickFavouriteIcon(row),
          }}
          ></NumerableTable>)
        }
      </React.Fragment>
    )
  }
}

// Creates an array of objects representing the header cells for the advisers list table, based on the given props
const createNexusAdviserClientsHeadersData = props => {
  // console.log('createNexusAdviserClientsHeadersData props:', props)
  const { portrait, showLogos, the350club } = props

  const starHead = headCell({
    align: left,
    id: COLUMN.STAR,
    width: LAYOUT.STAR_WIDTH,
  })

  const skipMe = headCell({
    id: COLUMN.SKIP_ME,
    label: "",
    width: "5%",
    sortLabel: false,
  })

  const adviserClient = headCell({ align: left, colSpan: 2, id: COLUMN.ADVISER_CLIENT, label: "Client", tooltipTitle: "" })
  const adviserClientP = headCell({ align: left, id: COLUMN.ADVISER_CLIENT, label: "Client", paddingRight: 2, tooltipTitle: "" })

  const asAuditor = headCell({ align: center, id: COLUMN.AS_AUDITOR })
  const asRecruitingAdviser = headCell({ align: center, id: COLUMN.AS_RECRUITING_ADVISER })
  const asRemunerationAdviser = headCell({ align: center, id: COLUMN.AS_REMUNERATION_ADVISER })
  const asExternalReviewer = headCell({ align: center, id: COLUMN.AS_EXTERNAL_REVIEWER })

  const asAuditorP = headCell({ align: center, id: COLUMN.AS_AUDITOR, label: "Auditor", paddingRight: 2 })
  const asRecruitingAdviserP = headCell({ align: center, id: COLUMN.AS_RECRUITING_ADVISER, label: "Recr. Adviser", paddingRight: 2 })
  const asRemunerationAdviserP = headCell({ align: center, id: COLUMN.AS_REMUNERATION_ADVISER, label: "Remun. Adviser", paddingRight: 2 })
  const asExternalReviewerP = headCell({ align: center, id: COLUMN.AS_EXTERNAL_REVIEWER, label: "Board Reviewer", paddingRight: 2 })

  let headCells = portrait
    ? [starHead, adviserClientP, asAuditorP, asRecruitingAdviserP, asRemunerationAdviserP, asExternalReviewerP]
    : [starHead, adviserClient, asAuditor, asRecruitingAdviser, asRemunerationAdviser, asExternalReviewer]

  if (the350club)
    headCells.shift() // Remove starHead

  addPaddingRightToLastCell(headCells)
  // console.log('headCells:', headCells)
  return headCells
}

//#region Rows
// Returns an array of objects representing the rows in the advisers list table, created from the given props
const createAdvisersListRowData = props => {
  // console.log('createAdvisersListRowData props:', props)
  const { clients, stats } = props
  let rows = []
  const tooltipTitle = ""

  initStats2(stats2)

  clients.forEach(client => {
    rows.push(createAdviserListRow({ ...props, client, tooltipTitle }))
    // ++stats.numRows
  })
  // console.log('rows:', rows)
  return rows
}

// Returns an object representing a row in the advisers list table for the given item
const createAdviserListRow = props => {
  // console.log("createAdviserListRow props: ", props)
  const { adviserName, client, portrait, stats } = props

  // Returns an object with 'value' and 'sortValue' properties for the given adviser role
  const valueAndSort = isAdviser => {
    return {
      align: center,
      value: isAdviser ? LAYOUT.TICK : "",
      sortValue: isAdviser ? 1 : 0,
    }
  }

  // Returns true if the given adviser name is found in the given value (which may be a comma-separated list of adviser names)
  const hasAdviser = (value, adviserName) => {
    if (!value)
      return false

    return value
      .split(',')
      .map(name => name.trim())
      .includes(adviserName)
  }

  const cellSpec = {
    [COLUMN.ADVISER_CLIENT]: {
      colSpan: portrait ? 1 : 2,
      align: left,
      type: string,
      value: client.names[0],
    },

    [COLUMN.AS_AUDITOR]: {
      ...valueAndSort(hasAdviser(
        client.advisers.auditor,
        adviserName
      )),
    },

    [COLUMN.AS_RECRUITING_ADVISER]: {
      ...valueAndSort(hasAdviser(
        client.advisers.recruitingAdviser,
        adviserName
      )),
    },

    [COLUMN.AS_REMUNERATION_ADVISER]: {
      ...valueAndSort(hasAdviser(
        client.advisers.remunerationAdviser,
        adviserName
      )),
    },

    [COLUMN.AS_EXTERNAL_REVIEWER]: {
      ...valueAndSort(hasAdviser(
        client.advisers.lastReviewFacilitator,
        adviserName
      )),
    },
  }

  const pr = portrait ? { paddingRight: 2 } : {}
  const adviserClient = getCellWithTooltip({ ...cellSpec[COLUMN.ADVISER_CLIENT], ...pr })
  const asAuditor = getCellWithTooltip({ ...cellSpec[COLUMN.AS_AUDITOR], ...pr })
  const asRecruitingAdviser = getCellWithTooltip({ ...cellSpec[COLUMN.AS_RECRUITING_ADVISER], ...pr })
  const asRemunerationAdviser = getCellWithTooltip({ ...cellSpec[COLUMN.AS_REMUNERATION_ADVISER], ...pr })
  const asExternalReviewer = getCellWithTooltip({ ...cellSpec[COLUMN.AS_EXTERNAL_REVIEWER], ...pr })
  // const totalClients = getCellWithTooltip(cellSpec[COLUMN.TOTAL_CLIENTS])
  // setStats(item.auditors.length, stats.asAuditor, true)
  // NB: 'setStats()' accepts both a number or bool for its 1st parameter
  // since the unary '+' operator converts boolean into a number
  // @ts-ignore
  // setStats(cellSpec[COLUMN.COMPANY].value, stats2[COLUMN.COMPANY], true)

  setStats(asAuditor.sortValue, stats.asAuditor, true)
  setStats(asRecruitingAdviser.sortValue, stats.asRecruitingAdviser, true)
  setStats(asRemunerationAdviser.sortValue, stats.asRemunerationAdviser, true)
  setStats(asExternalReviewer.sortValue, stats.asExternalReviewer, true)
  // setStats(item.totalClients, stats.totalClients, true)

  const cellWithTooltips = [adviserClient, asAuditor, asRecruitingAdviser, asRemunerationAdviser, asExternalReviewer]

  addPaddingRightToLastCell(cellWithTooltips)

  return {
    _id: client.ids[0],
    category: CATEGORY.COMPANY,
    cellWithTooltips,
    iconColor: COLOR.GOOD,
    ids: client.ids,
    index: client.index,
    isFavourite: client.isFavourite,
    link: client.ids.length > 0 ? `/company/${client.ids[0]}` : null,
    title: client.names[0],
  }
}
//#endregion Rows

const createNexusAdviserClientsFootersData = props => {
  // console.log('createNexusAdviserClientsFootersData props:', props)
  const { clients, portrait, stats, the350club } = props

  let column = null
  const tooltip = ""
  const lseCompaniesNameFoot = headCell({ id: COLUMN.NAME, align: left, label: `${formatInteger(stats.numRows)} ${stats.numRows === 1 ? JARGON.COMPANY : JARGON.COMPANIES}` })

  for (const property in stats2)
    calcStats(stats2[property])

  //#region column cells
  column = COLUMN.ADVISER_CLIENT
  const suffix = ` ${clients.length === 1 ? JARGON.ADVISER_CLIENT : JARGON.ADVISER_CLIENT + "s"}`
  const adviserClient = {
    total: getCell({ align: left, column, suffix, tooltip, type: integer, value: clients.length }),// stats2[column].total }),
    mean: getCell({ column })
  }

  column = COLUMN.LEGEND
  const legend = {
    total: getCell({ align: right, column, label: "Total:" }),
    mean: getCell({ align: right, column, label: "Average:" }),
  }

  column = COLUMN.STAR
  const starFoot = {
    total: getCell({ column }),
    mean: getCell({ column })
  }

  column = COLUMN.AS_AUDITOR
  const asAuditor = {
    total: getCell({ align: center, column, tooltip, type: integer, value: stats.asAuditor.total }),
    mean: getCell({ align: center, column, tooltip, type: integer, value: stats.asAuditor.mean }),
  }

  column = COLUMN.AS_RECRUITING_ADVISER
  const asRecruitingAdviser = {
    total: getCell({ align: center, column, tooltip, type: integer, value: stats.asRecruitingAdviser.total }),
    mean: getCell({ align: center, column, tooltip, type: integer, value: stats.asRecruitingAdviser.mean }),
  }

  column = COLUMN.AS_REMUNERATION_ADVISER
  const asRemunerationAdviser = {
    total: getCell({ align: center, column, tooltip, type: integer, value: stats.asRemunerationAdviser.total }),
    mean: getCell({ align: center, column, tooltip, type: integer, value: stats.asRemunerationAdviser.mean }),
  }

  column = COLUMN.AS_EXTERNAL_REVIEWER
  const asExternalReviewer = {
    total: getCell({ align: center, column, tooltip, type: integer, value: stats.asExternalReviewer.total }),
    mean: getCell({ align: center, column, tooltip, type: integer, value: stats.asExternalReviewer.mean })
  }
  column = COLUMN.TOTAL_CLIENTS
  const totalClients = {
    total: getCell({ align: center, column, borderLeft: borderLeft, tooltip, type: integer, value: stats.totalClients.total }),
    mean: getCell({ align: center, column, borderLeft: borderLeft, tooltip, type: integer, value: stats.totalClients.mean })
  }
  //#endregion column cells

  const footCells = {
    totals: portrait
      ? [starFoot.total, adviserClient.total, asAuditor.total, asRecruitingAdviser.total, asRemunerationAdviser.total, asExternalReviewer.total]
      : [starFoot.total, adviserClient.total, legend.total, asAuditor.total, asRecruitingAdviser.total, asRemunerationAdviser.total, asExternalReviewer.total],
    // means: portrait
    //   ? [starFoot.mean, adviserClient.mean, asExternalReviewer.mean] // portrait
    //   : [starFoot.mean, adviserClient.mean, legend.mean, asAuditor.mean, asRecruitingAdviser.mean, asRemunerationAdviser.mean, asExternalReviewer.mean], // landscape
  }

  addPaddingRightToLastCell(footCells.totals)
  // addPaddingRightToLastCell(footCells.means)
  return footCells
}

export default NexusAdviserClientsTable